import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { MENU_ITEMS, SETTINGS_PERMISSIONS } from '@config/constants';
import { useThemeStore } from '@store';
import { usePermission } from '@hooks';

export default function Sidebar({ isOpen, activeView, onViewChange, onClientModeToggle }) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const { hasAnyPermission, permissions } = usePermission();

  // Filtrar items del menú según permisos del usuario
  const visibleMenuItems = useMemo(() => {
    // Si no hay permisos cargados aún, mostrar todos (loading state)
    if (!permissions || permissions.length === 0) {
      return MENU_ITEMS;
    }

    return MENU_ITEMS.filter((item) => {
      // Si no tiene permisos requeridos, mostrar a todos
      if (!item.permissions || item.permissions.length === 0) {
        return true;
      }
      // Verificar si tiene al menos uno de los permisos requeridos
      return hasAnyPermission(item.permissions);
    });
  }, [permissions, hasAnyPermission]);

  // Verificar si puede ver configuración
  const canViewSettings = useMemo(() => {
    if (!permissions || permissions.length === 0) return true;
    return hasAnyPermission(SETTINGS_PERMISSIONS);
  }, [permissions, hasAnyPermission]);

  const sidebarStyle = {
    width: isOpen ? '256px' : '72px',
    background: isDark
      ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
      : 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    color: 'white',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  };

  const logoContainerStyle = {
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const logoIconStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    flexShrink: 0,
    objectFit: 'cover',
  };

  const navStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#334155 transparent',
    padding: '8px 12px',
  };

  const menuItemStyle = (isActive) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '4px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: isActive ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
    color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
  });

  const settingsButtonStyle = (isActive) => ({
    ...menuItemStyle(isActive),
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    marginTop: '8px',
    paddingTop: '16px',
  });

  const clientButtonStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    transition: 'opacity 0.2s',
  };

  return (
    <aside style={sidebarStyle}>
      {/* Logo */}
      <div style={logoContainerStyle}>
        <img
          src="/api/uploads/logoI.jpeg"
          alt="JIROX"
          style={logoIconStyle}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {isOpen && (
          <div>
            <h1 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>JIROX</h1>
            <p style={{ fontSize: '11px', opacity: 0.7, margin: 0 }}>Project Manager</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav style={navStyle}>
        {visibleMenuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onViewChange(item.id)}
            style={menuItemStyle(activeView === item.id)}
            onMouseOver={(e) => {
              if (activeView !== item.id) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = 'white';
              }
            }}
            onMouseOut={(e) => {
              if (activeView !== item.id) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }
            }}
          >
            <span style={{ fontSize: '18px' }}>{item.icon}</span>
            {isOpen && <span>{item.label}</span>}
          </button>
        ))}

        {/* Settings button - solo si tiene permiso */}
        {canViewSettings && (
          <button
            onClick={() => onViewChange('settings')}
            style={settingsButtonStyle(activeView === 'settings')}
            onMouseOver={(e) => {
              if (activeView !== 'settings') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = 'white';
              }
            }}
            onMouseOut={(e) => {
              if (activeView !== 'settings') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }
            }}
          >
            <span style={{ fontSize: '18px' }}>⚙️</span>
            {isOpen && <span>Configuración</span>}
          </button>
        )}
      </nav>

      {/* Client view button */}
      <div style={{ padding: '16px' }}>
        <button
          onClick={onClientModeToggle}
          style={clientButtonStyle}
          onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
        >
          <span>👁️</span>
          {isOpen && <span>Vista Cliente</span>}
        </button>
      </div>
    </aside>
  );
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  activeView: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
  onClientModeToggle: PropTypes.func.isRequired,
};
